import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Users, AlertTriangle, CheckCircle2, Clock, TrendingUp, ArrowRight } from 'lucide-react';
import { API_URL } from '@/services/apiClient';

interface TeacherWorkload {
  teacherId: string;
  name: string;
  assigned: number;
  resolved: number;
  open: number;
  resolutionRate: number;
  avgResolutionHours: number | null;
}

interface WorkloadData {
  teachers: TeacherWorkload[];
  unassigned: number;
}

export function WorkloadView({
  dm,
  onViewTeacher,
}: {
  dm: boolean;
  onViewTeacher?: (teacherName: string) => void;
}) {
  const [data, setData]       = useState<WorkloadData | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays]       = useState(30);

  const cardCls    = `border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls   = dm ? 'text-slate-100' : 'text-slate-800';
  const descCls    = dm ? 'text-slate-400' : 'text-slate-500';
  const bodyTxt    = dm ? 'text-slate-300' : 'text-slate-700';
  const mutedCls   = dm ? 'text-slate-500' : 'text-slate-400';
  const borderCls  = dm ? 'border-slate-700' : 'border-slate-200';
  const rowHover   = dm ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50';
  const dividerCls = dm ? 'divide-slate-700' : 'divide-slate-100';

  const fetch_ = useCallback(async () => {
    setLoading(true);
    const token = sessionStorage.getItem('authToken');
    try {
      const res = await fetch(`${API_URL}/dashboard/school-admin/workload?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch {/* */} finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const totalOpen        = data?.teachers.reduce((s, t) => s + t.open, 0) ?? 0;
  const totalResolved    = data?.teachers.reduce((s, t) => s + t.resolved, 0) ?? 0;
  const avgResolutionRate = data?.teachers.length
    ? Math.round(data.teachers.reduce((s, t) => s + t.resolutionRate, 0) / data.teachers.length)
    : 0;

  return (
    <div className="space-y-5" data-dm={dm}>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className={`text-lg font-semibold ${titleCls}`}>Teacher Workload</h2>
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`btn-glass btn-glass-pill text-sm ${days === d ? 'btn-glass-blue' : ''}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Open Alerts',         value: totalOpen,            icon: AlertTriangle, color: 'red',     sub: 'Across all teachers' },
          { label: 'Resolved',            value: totalResolved,        icon: CheckCircle2,  color: 'emerald', sub: `Last ${days} days`    },
          { label: 'Unassigned',          value: data?.unassigned ?? 0, icon: Users,        color: 'amber',   sub: 'Need assignment'      },
          { label: 'Avg Resolution Rate', value: `${avgResolutionRate}%`, icon: TrendingUp, color: 'blue',    sub: 'School-wide'          },
        ].map((s, i) => (
          <Card key={i} className={cardCls}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2.5">
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${dm ? `bg-${s.color}-900/50` : `bg-${s.color}-100`}`}>
                  <s.icon className={`h-4 w-4 text-${s.color}-500`} />
                </div>
                <div>
                  <p className={`text-xs ${descCls}`}>{s.label}</p>
                  <p className={`text-xl font-bold ${
                    s.color === 'red'   && Number(s.value) > 0 ? 'text-red-500'   :
                    s.color === 'amber' && Number(s.value) > 0 ? 'text-amber-500' : titleCls
                  }`}>{s.value}</p>
                  <p className={`text-xs ${mutedCls}`}>{s.sub}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Unassigned alert banner */}
      {(data?.unassigned ?? 0) > 0 && (
        <div className={`rounded-xl border p-4 flex items-start gap-3 ${
          dm ? 'bg-amber-900/20 border-amber-700' : 'bg-amber-50 border-amber-200'
        }`}>
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className={`text-sm font-semibold ${dm ? 'text-amber-400' : 'text-amber-800'}`}>
              {data?.unassigned} alert{data!.unassigned !== 1 ? 's' : ''} have no assigned teacher
            </p>
            <p className={`text-xs mt-0.5 ${dm ? 'text-amber-500' : 'text-amber-600'}`}>
              Go to Incident Management to assign these alerts to teachers.
            </p>
          </div>
        </div>
      )}

      {/* Teacher workload table */}
      <Card className={cardCls}>
        <CardHeader className="pb-2">
          <CardTitle className={`text-base ${titleCls}`}>Teacher Workload Breakdown</CardTitle>
          <CardDescription className={descCls}>
            Alert load and resolution performance per teacher
            {onViewTeacher && <span className={`ml-2 ${mutedCls}`}>· Click a row to view their incidents</span>}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : !data?.teachers.length ? (
            <div className={`flex flex-col items-center justify-center h-40 ${mutedCls}`}>
              <Users className="h-10 w-10 mb-2 opacity-40" />
              <p className="text-sm">No teachers found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b text-xs font-semibold uppercase tracking-wider ${borderCls} ${descCls}`}>
                    <th className="px-4 py-3 text-left">Teacher</th>
                    <th className="px-4 py-3 text-center">Assigned</th>
                    <th className="px-4 py-3 text-center">Open</th>
                    <th className="px-4 py-3 text-center">Resolved</th>
                    <th className="px-4 py-3 text-center">Resolution Rate</th>
                    <th className="px-4 py-3 text-center">Avg Time</th>
                    {onViewTeacher && <th className="px-4 py-3" />}
                  </tr>
                </thead>
                <tbody className={`divide-y ${dividerCls}`}>
                  {data.teachers.map(t => (
                    <tr
                      key={t.teacherId}
                      className={`transition-colors ${rowHover} ${onViewTeacher ? 'cursor-pointer' : ''}`}
                      onClick={() => onViewTeacher?.(t.name)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${dm ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                            {t.name.charAt(0).toUpperCase()}
                          </div>
                          <span className={`font-medium ${bodyTxt}`}>{t.name}</span>
                        </div>
                      </td>
                      <td className={`px-4 py-3 text-center font-medium ${bodyTxt}`}>{t.assigned}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-semibold ${t.open > 5 ? 'text-red-500' : t.open > 2 ? 'text-amber-500' : mutedCls}`}>
                          {t.open}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-emerald-500 font-medium">{t.resolved}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-semibold ${
                          t.resolutionRate >= 80 ? 'text-emerald-500' :
                          t.resolutionRate >= 50 ? 'text-amber-500'   : 'text-red-500'
                        }`}>
                          {t.resolutionRate}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Clock className={`h-3.5 w-3.5 ${mutedCls}`} />
                          <span className={`text-xs ${mutedCls}`}>
                            {t.avgResolutionHours != null ? `${t.avgResolutionHours}h` : '—'}
                          </span>
                        </div>
                      </td>
                      {onViewTeacher && (
                        <td className="px-4 py-3 text-right">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity ${dm ? 'text-blue-400' : 'text-blue-600'}`}>
                            View <ArrowRight className="h-3 w-3" />
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
